import React from 'react';
import YesNoField from './FieldTypes/YesNoField';
import MultipleChoiceField from './FieldTypes/MultipleChoiceField';
import CheckboxesField from './FieldTypes/CheckboxesField';
import TextInputField from './FieldTypes/TextInputField';
import TextareaField from './FieldTypes/TextareaField';
import NumberInputField from './FieldTypes/NumberInputField';
import RatingScaleField from './FieldTypes/RatingScaleField';
import DatePickerField from './FieldTypes/DatePickerField';
import TimePickerField from './FieldTypes/TimePickerField';
import PhoneInputField from './FieldTypes/PhoneInputField';

import SectionTitleField from './FieldTypes/SectionTitleField';
import TextBlockField from './FieldTypes/TextBlockField';
import DividerField from './FieldTypes/DividerField';
import { Box, Typography } from '@mui/material';
import { ComponentType, SurveyComponent } from '../../../models/SurveyTemplates';
import EmailInputField from './FieldTypes/EmailInputField';
import ImagePickerField from './FieldTypes/ImagePickerField';

interface FieldRendererProps {
    field: SurveyComponent;
    value?: any;
    onChange?: (value: any) => void;
    disabled?: boolean;
    inline?: boolean;
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
    field,
    value,
    onChange,
    disabled = false,
    inline = false
}) => {
    // Field types that don't need labels (they are layout/display components)
    const noLabelFieldTypes = [
        // ComponentType.SECTION_TITLE,
        // ComponentType.TEXT_BLOCK,
        ComponentType.DIVIDER
    ];

    const shouldShowLabel = !noLabelFieldTypes.includes(field.type);

    const renderField = () => {
        switch (field.type) {
            case ComponentType.YES_NO:
                return (
                    <YesNoField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.MULTIPLE_CHOICE:
                return (
                    <MultipleChoiceField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.CHECKBOXES:
                return (
                    <CheckboxesField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.TEXT_INPUT:
                return (
                    <TextInputField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.TEXTAREA:
                return (
                    <TextareaField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.NUMBER_INPUT:
                return (
                    <NumberInputField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.RATING_SCALE:
                return (
                    <RatingScaleField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.IMAGE_UPLOAD:
                return (
                    <ImagePickerField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.DATE_PICKER:
                return (
                    <DatePickerField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.TIME_PICKER:
                return (
                    <TimePickerField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.EMAIL_INPUT:
                return (
                    <EmailInputField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.PHONE_INPUT:
                return (
                    <PhoneInputField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.SECTION_TITLE:
                return (
                    <SectionTitleField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.TEXT_BLOCK:
                return (
                    <TextBlockField
                        field={field}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case ComponentType.DIVIDER:
                return (
                    <DividerField
                        field={field}
                    />
                );
            default:
                return (
                    <Box sx={{ p: 2, border: '2px dashed #ccc', textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Unknown field type: {field.type}
                        </Typography>
                    </Box>
                );
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            {shouldShowLabel && field.label && (
                <Typography 
                    variant="body1" 
                    component="label" 
                    sx={{ 
                        mb: 1, 
                        display: 'block', 
                        fontWeight: 500,
                        color: 'text.primary' 
                    }}
                >
                    {field.label}
                    {field.required && (
                        <Typography 
                            component="span" 
                            color="error" 
                            sx={{ ml: 0.5 }}
                        >
                            *
                        </Typography>
                    )}
                </Typography>
            )}
            {field.description && (
                <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 1 }}
                >
                    {field.description}
                </Typography>
            )}
            {renderField()}
        </Box>
    );
};

export default FieldRenderer;
